import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Loader2, Save, Upload, ExternalLink, AlertTriangle, Clock,
  Search, Info, Image as ImageIcon,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CommodityTagInput from '../components/CommodityTagInput';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';
import usePageTitle from '../hooks/usePageTitle';

const BRAND_BLUE = '#2B4B6A';

// Comma-separated text <-> array helpers for the list fields
const toText = (v) => (Array.isArray(v) ? v.join(', ') : (v || ''));
const toList = (v) => String(v || '').split(',').map((s) => s.trim()).filter(Boolean);

const DEFAULT_LOGO_BG = '#F3F4F6';

// Preset backgrounds for the logo tile — light gray first, since most logos are
// designed for a light surface.
const LOGO_BGS = [
  { value: '#F3F4F6', label: 'Light gray' },
  { value: '#FFFFFF', label: 'White' },
  { value: '#2B4B6A', label: 'Trados blue' },
  { value: '#1E3550', label: 'Deep navy' },
  { value: '#111827', label: 'Near black' },
  { value: '#F58A5A', label: 'Trados orange' },
];

const EMPTY = {
  name: '', slug: '', logo_url: '', logo_bg: DEFAULT_LOGO_BG, website: '',
  country: '', city: '', address: '', description: '',
  catalog_commodities: [], catalog_minerals: [], other_trade: [],
  emails: '', phones: '', principal_name: '', principal_email: '',
};

// Defined at module scope on purpose. Declaring these inside the component gives
// them a new identity on every render, so React unmounts and remounts the inputs
// and typing loses focus after each keystroke.
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

const Shell = ({ children }) => (
  <Sidebar>
    <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
      <div className="max-w-3xl mx-auto space-y-4">{children}</div>
    </div>
  </Sidebar>
);

const EditBusinessInfo = () => {
  usePageTitle('Edit Business Information');

  const [state, setState] = useState('loading'); // loading | none | pending | ready | error
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugCheck, setSlugCheck] = useState(null); // {available, reason, checking}
  const [catalog, setCatalog] = useState({ commodities: [], minerals: [] });
  const [logoPreview, setLogoPreview] = useState('');   // what the public sees
  const [logoBroken, setLogoBroken] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [res, filters, cat] = await Promise.all([
        api.get('/directory/my-listing'),
        api.get('/public/directory/filters').catch(() => ({ data: { countries: [] } })),
        api.get('/public/directory/trade-catalog').catch(() => ({ data: { commodities: [], minerals: [] } })),
      ]);
      setCountries(filters.data.countries || []);
      setCatalog(cat.data || { commodities: [], minerals: [] });

      const l = res.data.listing;
      if (!l) { setState('none'); return; }
      setListing(l);
      if (!l.is_owner) { setState('pending'); return; }

      setLogoPreview(l.resolved_logo_url || l.logo_url || '');
      setLogoBroken(false);
      setForm({
        name: l.name || '',
        slug: l.slug || '',
        logo_bg: l.logo_bg || DEFAULT_LOGO_BG,
        logo_url: l.logo_url || '',
        website: l.website || '',
        country: l.country || '',
        city: l.city || '',
        address: l.address || '',
        description: l.description || '',
        catalog_commodities: l.catalog_commodities || [],
        catalog_minerals: l.catalog_minerals || [],
        other_trade: l.other_trade || [],
        emails: toText(l.emails),
        phones: toText(l.phones),
        principal_name: l.principal_name || '',
        principal_email: l.principal_email || '',
      });
      setState('ready');
    } catch (err) {
      setState('error');
      toast.error('Could not load your business information');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Check the chosen address as the user types (debounced).
  useEffect(() => {
    const value = (form.slug || '').trim();
    if (state !== 'ready' || !value || value === listing?.slug) { setSlugCheck(null); return undefined; }
    setSlugCheck({ checking: true });
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/directory/slug-available', { params: { slug: value } });
        setSlugCheck({ ...res.data, checking: false });
      } catch {
        setSlugCheck({ available: false, reason: 'Could not check that address', checking: false });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.slug, state, listing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`Image is ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum is 2MB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/directory/my-listing/logo', fd);
      set('logo_url', res.data.logo_url);
      setLogoPreview(res.data.logo_url);
      setLogoBroken(false);
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const slugChanged = form.slug.trim() && form.slug.trim() !== listing.slug;
      if (slugChanged && slugCheck && slugCheck.available === false) {
        toast.error(slugCheck.reason || 'Choose a different address');
        setSaving(false);
        return;
      }
      const res = await api.put(`/directory/${listing.slug}`, {
        name: form.name.trim(),
        ...(slugChanged ? { slug: form.slug.trim() } : {}),
        logo_bg: form.logo_bg || DEFAULT_LOGO_BG,
        logo_url: form.logo_url || null,
        website: form.website.trim() || null,
        country: form.country || null,
        city: form.city.trim() || null,
        address: form.address.trim() || null,
        description: form.description.trim() || null,
        commodities: [...form.catalog_commodities, ...form.catalog_minerals, ...form.other_trade],
        emails: toList(form.emails),
        phones: toList(form.phones),
        principal_name: form.principal_name.trim() || null,
        principal_email: form.principal_email.trim() || null,
      });
      toast.success('Business information saved');
      // slug may have been normalised server-side — resync so the URL preview
      // and future saves use the real value
      if (res?.data?.slug && res.data.slug !== listing.slug) {
        setListing((l) => ({ ...l, slug: res.data.slug }));
        setForm((f) => ({ ...f, slug: res.data.slug }));
        setSlugCheck(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          <p className="text-sm">Loading your business information...</p>
        </div>
      </Shell>
    );
  }

  if (state === 'error') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-red-600">Could not load your business information</p>
          <button onClick={load} className="mt-3 px-3 py-1.5 text-xs font-medium text-white rounded-lg"
                  style={{ backgroundColor: BRAND_BLUE }}>Try again</button>
        </div>
      </Shell>
    );
  }

  if (state === 'none') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">No business listing yet</h1>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto">
            Find your company in the Trados directory and claim it. Once an admin approves
            the claim you can edit your business information here.
          </p>
          <Link to="/directory"
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: BRAND_BLUE }}>
            <Search className="w-4 h-4" /> Find my business
          </Link>
        </div>
      </Shell>
    );
  }

  if (state === 'pending') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Your claim is awaiting approval</h1>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto">
            You claimed <strong>{listing?.name || 'this business'}</strong>. Once an admin approves it,
            this page becomes editable.
          </p>
          {listing?.slug && (
            <a href={`/directory/${listing.slug}`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium hover:underline"
               style={{ color: BRAND_BLUE }}>
              View listing <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            Edit Business Information
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            This is your public directory listing. It is also reused to pre-fill your tier verification.
          </p>
        </div>
        <a href={`/directory/${listing.slug}`} target="_blank" rel="noopener noreferrer"
           className="text-sm font-medium hover:underline flex items-center gap-1" style={{ color: BRAND_BLUE }}>
          View public page <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-900">
          Saving here also updates the company details used during tier verification, so you
          only enter them once.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-4">

        {/* Company */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Company</h2>

          <Field label="Company name *">
            <input data-testid="bi-name" className={input} value={form.name}
                   onChange={(e) => set('name', e.target.value)} required />
          </Field>

          <Field label="Directory address"
                 hint="Your public URL. Changing it keeps the old address working, so existing links never break.">
            <div className="flex items-stretch">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-xs text-gray-500 whitespace-nowrap">
                trados.app/directory/
              </span>
              <input
                data-testid="bi-slug"
                className={`flex-1 min-w-0 px-3 py-2 border rounded-r-lg text-sm focus:outline-none focus:ring-2 ${
                  slugCheck && slugCheck.available === false
                    ? 'border-red-400 focus:ring-red-400'
                    : slugCheck && slugCheck.available === true
                      ? 'border-green-400 focus:ring-green-400'
                      : 'border-gray-300 focus:ring-blue-500'
                }`}
                value={form.slug}
                onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              />
            </div>
            {slugCheck?.checking && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Checking availability...
              </p>
            )}
            {slugCheck && !slugCheck.checking && slugCheck.available === true && (
              <p className="text-xs text-green-600 mt-1" data-testid="bi-slug-ok">
                {slugCheck.slug} is available
              </p>
            )}
            {slugCheck && !slugCheck.checking && slugCheck.available === false && (
              <p className="text-xs text-red-600 mt-1" data-testid="bi-slug-bad">{slugCheck.reason}</p>
            )}
          </Field>

          <Field label="Logo" hint="PNG, JPG, WebP or SVG. Maximum 2MB.">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0"
                   style={{ background: form.logo_bg || DEFAULT_LOGO_BG }}>
                {logoPreview && !logoBroken ? (
                  <img
                    src={resolveFileUrl(logoPreview, process.env.REACT_APP_BACKEND_URL || '')}
                    alt="Company logo"
                    onError={() => setLogoBroken(true)}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleLogo}
                       data-testid="bi-logo" className="hidden" id="bi-logo-input" />
                <label htmlFor="bi-logo-input"
                       className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Upload logo'}
                </label>
              </div>
            </div>
          </Field>

          {logoBroken && (
            <p className="text-xs text-amber-600 -mt-2">
              The address currently saved in your logo field is not an image, so no logo is
              shown. Upload one above to fix it.
            </p>
          )}

          <Field label="Logo background" hint="The tile behind your logo. Light gray suits most logos; pick a dark tone for white logos.">
            <div className="flex flex-wrap items-center gap-2">
              {LOGO_BGS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  data-testid={`bi-bg-${c.value.replace('#', '')}`}
                  onClick={() => set('logo_bg', c.value)}
                  className={`w-9 h-9 rounded-lg border-2 transition ${
                    (form.logo_bg || DEFAULT_LOGO_BG).toUpperCase() === c.value.toUpperCase()
                      ? 'border-blue-500 scale-110' : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ background: c.value }}
                />
              ))}
              <label className="flex items-center gap-2 ml-1 text-xs text-gray-500 cursor-pointer">
                <input
                  type="color"
                  data-testid="bi-bg-custom"
                  value={form.logo_bg || DEFAULT_LOGO_BG}
                  onChange={(e) => set('logo_bg', e.target.value)}
                  className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer bg-white p-0.5"
                />
                Custom
              </label>
            </div>
          </Field>

          <Field label="Website">
            <input data-testid="bi-website" className={input} value={form.website} placeholder="https://example.com"
                   onChange={(e) => set('website', e.target.value)} />
          </Field>

          <Field label="About us" hint="Shown on your public directory page.">
            <textarea data-testid="bi-description" className={input} rows={5} value={form.description}
                      onChange={(e) => set('description', e.target.value)} />
          </Field>

          <Field
            label="Commodities we trade"
            hint="Choose from the standard list so buyers can filter for exactly what you sell."
          >
            <CommodityTagInput
              testId="bi-commodities"
              options={catalog.commodities}
              allowCreate={false}
              placeholder="Search commodities — e.g. Sugar, Soybeans, Crude Oil"
              value={form.catalog_commodities}
              onChange={(v) => set('catalog_commodities', v)}
            />
          </Field>

          <Field
            label="Minerals & metals we trade"
            hint="Choose from the standard list."
          >
            <CommodityTagInput
              testId="bi-minerals"
              options={catalog.minerals}
              allowCreate={false}
              placeholder="Search minerals — e.g. Gold, Iron Ore, Lithium"
              value={form.catalog_minerals}
              onChange={(v) => set('catalog_minerals', v)}
            />
          </Field>

          <Field
            label="Other"
            hint="Anything not covered above. Free text — only use this when nothing in the two lists fits."
          >
            <CommodityTagInput
              testId="bi-other-trade"
              placeholder="Type and press Enter"
              value={form.other_trade}
              onChange={(v) => set('other_trade', v)}
            />
          </Field>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Location</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Country">
              <input data-testid="bi-country" className={input} list="bi-country-list" value={form.country}
                     onChange={(e) => set('country', e.target.value)} />
              <datalist id="bi-country-list">
                {countries.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="City">
              <input data-testid="bi-city" className={input} value={form.city}
                     onChange={(e) => set('city', e.target.value)} />
            </Field>
          </div>
          <Field label="Address" hint="Street address. Used to pre-fill your company address during verification.">
            <textarea data-testid="bi-address" className={input} rows={2} value={form.address}
                      onChange={(e) => set('address', e.target.value)} />
          </Field>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Contact</h2>
          <p className="text-xs text-gray-400 -mt-2">
            Only visible to signed-in Trados users, never to anonymous visitors.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Emails" hint="Comma separated">
              <input data-testid="bi-emails" className={input} value={form.emails}
                     onChange={(e) => set('emails', e.target.value)} />
            </Field>
            <Field label="Phones" hint="Comma separated">
              <input data-testid="bi-phones" className={input} value={form.phones}
                     onChange={(e) => set('phones', e.target.value)} />
            </Field>
            <Field label="Main contact name">
              <input data-testid="bi-principal" className={input} value={form.principal_name}
                     onChange={(e) => set('principal_name', e.target.value)} />
            </Field>
            <Field label="Main contact email">
              <input data-testid="bi-principal-email" className={input} value={form.principal_email}
                     onChange={(e) => set('principal_email', e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="bi-save"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: BRAND_BLUE }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          <button type="button" onClick={load} disabled={saving}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            Discard changes
          </button>
        </div>
      </form>
    </Shell>
  );
};

export default EditBusinessInfo;
